const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DAY_LABELS = {
  monday: 'Lundi', tuesday: 'Mardi', wednesday: 'Mercredi',
  thursday: 'Jeudi', friday: 'Vendredi', saturday: 'Samedi', sunday: 'Dimanche',
};

const DAY_ABBR = {
  monday: 'Lun', tuesday: 'Mar', wednesday: 'Mer',
  thursday: 'Jeu', friday: 'Ven', saturday: 'Sam', sunday: 'Dim',
};

function timeToMin(t) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function fmtTime(t) {
  const [h, m] = t.split(':').map(Number);
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
}

function getNow(timezone) {
  const now = new Date();
  // 'en-US' weekday long → "Monday" → lowercase
  const dayName = now.toLocaleDateString('en-US', { timeZone: timezone, weekday: 'long' }).toLowerCase();
  // 'fr-FR' HH:MM
  const timeStr = now.toLocaleTimeString('fr-FR', {
    timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false,
  });
  // 'sv-SE' gives YYYY-MM-DD
  const dateStr = now.toLocaleDateString('sv-SE', { timeZone: timezone });
  return { dayName, timeStr, dateStr };
}

function isExceptionallyClosed(closedDays, dateStr) {
  return (closedDays || []).some((entry) => {
    if (entry.oh_closed_type === 'single') {
      return entry.oh_closed_date_from === dateStr;
    }
    if (entry.oh_closed_type === 'range') {
      const to = entry.oh_closed_date_to || entry.oh_closed_date_from;
      return dateStr >= entry.oh_closed_date_from && dateStr <= to;
    }
    return false;
  });
}

function checkOpen(weekly, dayName, timeStr) {
  const minutes = timeToMin(timeStr);
  const slots = (weekly || []).filter((s) => s.oh_day === dayName);

  for (const slot of slots) {
    const open  = timeToMin(slot.oh_open);
    const close = timeToMin(slot.oh_close);
    if (minutes < open || minutes >= close) continue;

    if (slot.oh_has_break) {
      const bStart = timeToMin(slot.oh_break_start);
      const bEnd   = timeToMin(slot.oh_break_end);
      if (minutes >= bStart && minutes < bEnd) continue;
    }
    return true;
  }
  return false;
}

function buildScheduleHTML(weekly) {
  // First slot per day (multiple rows per day not condensed)
  const dayMap = {};
  (weekly || []).forEach((slot) => {
    if (!dayMap[slot.oh_day]) dayMap[slot.oh_day] = slot;
  });

  // Group consecutive days with identical schedule
  const groups = [];
  let cur = null;

  for (const day of DAY_ORDER) {
    if (!dayMap[day]) { cur = null; continue; }

    const slot = dayMap[day];
    const key = [
      slot.oh_open, slot.oh_close,
      slot.oh_has_break ? `${slot.oh_break_start}-${slot.oh_break_end}` : '',
    ].join('|');

    if (cur && cur.key === key) {
      cur.days.push(day);
    } else {
      cur = { key, days: [day], slot };
      groups.push(cur);
    }
  }

  const lines = groups.map(({ days, slot }) => {
    let dayLabel;
    if (days.length === 1)      dayLabel = DAY_LABELS[days[0]];
    else if (days.length === 2) dayLabel = `${DAY_ABBR[days[0]]} et ${DAY_ABBR[days[1]]}`;
    else                        dayLabel = `${DAY_ABBR[days[0]]} au ${DAY_ABBR[days[days.length - 1]]}`;

    let timeLabel = `${fmtTime(slot.oh_open)} – ${fmtTime(slot.oh_close)}`;
    if (slot.oh_has_break) {
      timeLabel += ` (pause ${fmtTime(slot.oh_break_start)} – ${fmtTime(slot.oh_break_end)})`;
    }

    return `<div class="OpeningHours-row"><span class="OpeningHours-day">${dayLabel}</span><span class="OpeningHours-time">${timeLabel}</span></div>`;
  });

  // Closed days of the week
  const openDays = new Set((weekly || []).map((s) => s.oh_day));
  const closedWeekDays = DAY_ORDER.filter((d) => !openDays.has(d));

  if (closedWeekDays.length && closedWeekDays.length < 7) {
    const label = closedWeekDays.map((d) => DAY_ABBR[d]).join(', ');
    lines.push(
      `<div class="OpeningHours-row -closed"><span class="OpeningHours-day">${label}</span><span class="OpeningHours-time">Fermé</span></div>`,
    );
  }

  return lines.join('');
}

function updateEl(el) {
  const timezone = el.dataset.timezone || 'Europe/Paris';
  let weekly = [], closed = [];

  try { weekly = JSON.parse(el.dataset.weekly || '[]'); } catch (_) { /* noop */ }
  try { closed = JSON.parse(el.dataset.closed  || '[]'); } catch (_) { /* noop */ }

  const { dayName, timeStr, dateStr } = getNow(timezone);
  const exceptionallyClose = isExceptionallyClosed(closed, dateStr);
  const currentlyOpen      = !exceptionallyClose && checkOpen(weekly, dayName, timeStr);

  el.dataset.status = currentlyOpen ? 'open' : 'closed';

  const dot      = el.querySelector('.OpeningHours-statusDot');
  const timeEl   = el.querySelector('.OpeningHours-statusTime');
  const labelEl  = el.querySelector('.OpeningHours-statusLabel');
  const schedule = el.querySelector('.OpeningHours-schedule');

  if (timeEl)  timeEl.textContent  = timeStr;
  if (labelEl) labelEl.textContent = currentlyOpen ? 'Ouvert' : 'Fermé';
  if (dot) {
    dot.classList.toggle('-open',   currentlyOpen);
    dot.classList.toggle('-closed', !currentlyOpen);
  }

  // Schedule built once (static data, no need to rebuild every minute)
  if (schedule && !schedule.dataset.built) {
    schedule.innerHTML    = buildScheduleHTML(weekly);
    schedule.dataset.built = '1';
  }
}

const openingHours = {
  load() {
    document.addEventListener('DOMContentLoaded', () => {
      const els = document.querySelectorAll('.OpeningHours');
      if (!els.length) return;

      els.forEach(updateEl);
      setInterval(() => els.forEach(updateEl), 60_000);
    });
  },
};

export default openingHours;
